import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, Table } from 'antd';

import {
  DashboardSummary,
  DashboardTicket,
} from '@totem/components/ticketing/ticketDashboard/types';
import TagDisplay, {
  TICKET_PRIORITY_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';

type Props = {
  tickets: DashboardTicket[];
  summary: DashboardSummary;
};

const PRIORITY_ORDER = ['P1', 'P2', 'P3', 'P4', 'P5'];
const AGE_BINS = [0, 5, 10, 15, 20, 30, 45, 60, 90];

const subTitle = (text: string) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 400,
      color: 'var(--ant-color-text-secondary)',
    }}
  >
    {text}
  </span>
);

const SlaTab = ({ tickets, summary }: Props) => {
  const slaRows = useMemo(
    () =>
      PRIORITY_ORDER.map((pri) => {
        const sub = tickets.filter((tkt) => tkt.p === pri);
        const brCount = sub.filter((tkt) => tkt.br === 1).length;
        const r24Count = sub.filter((tkt) => tkt.r24 === 1).length;
        const avgD = sub.length
          ? (sub.reduce((acc, tkt) => acc + tkt.ageD, 0) / sub.length).toFixed(
              1,
            )
          : '—';
        const pct = sub.length ? Math.round((100 * brCount) / sub.length) : 0;
        const thrH = summary.slaPri[pri]?.thrH ?? '—';
        return {
          pri,
          thrH,
          open: sub.length,
          br: brCount,
          r24: r24Count,
          avgD,
          pct,
        };
      }),
    [tickets, summary.slaPri],
  );

  const slaColumns = [
    {
      title: 'Priority',
      dataIndex: 'pri',
      key: 'pri',
      render: (val: string) => (
        <TagDisplay value={val} config={TICKET_PRIORITY_STYLES} />
      ),
    },
    {
      title: 'SLA (h)',
      dataIndex: 'thrH',
      key: 'thrH',
      align: 'right' as const,
    },
    {
      title: 'Open',
      dataIndex: 'open',
      key: 'open',
      align: 'right' as const,
      render: (val: number) => <strong>{val}</strong>,
    },
    {
      title: 'Past SLA',
      dataIndex: 'br',
      key: 'br',
      align: 'right' as const,
      render: (val: number) =>
        val > 0 ? (
          <span style={{ color: '#b91c1c', fontWeight: 700 }}>{val}</span>
        ) : (
          <span style={{ color: 'var(--ant-color-text-secondary)' }}>0</span>
        ),
    },
    {
      title: 'At risk 24h',
      dataIndex: 'r24',
      key: 'r24',
      align: 'right' as const,
      render: (val: number) =>
        val > 0 ? (
          <span style={{ color: '#f59e0b', fontWeight: 700 }}>{val}</span>
        ) : (
          <span style={{ color: 'var(--ant-color-text-secondary)' }}>0</span>
        ),
    },
    {
      title: '% Past SLA',
      dataIndex: 'pct',
      key: 'pct',
      align: 'right' as const,
      render: (val: number) => {
        const color =
          val >= 50
            ? '#b91c1c'
            : val >= 20
              ? '#ef4444'
              : val > 0
                ? '#f59e0b'
                : 'var(--ant-color-text-secondary)';
        return (
          <span style={{ color, fontWeight: val > 0 ? 700 : 400 }}>{val}%</span>
        );
      },
    },
    {
      title: 'Avg age (d)',
      dataIndex: 'avgD',
      key: 'avgD',
      align: 'right' as const,
    },
  ];

  // Response SLA chart — stacked bar per priority
  const respLabels = PRIORITY_ORDER;
  const respMet = PRIORITY_ORDER.map(
    (pri) =>
      tickets.filter((tkt) => tkt.p === pri && tkt.respMet === true).length,
  );
  const respMiss = PRIORITY_ORDER.map(
    (pri) =>
      tickets.filter((tkt) => tkt.p === pri && tkt.respMet === false).length,
  );

  const respOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: { bar: { borderRadius: 2, columnWidth: '55%' } },
    xaxis: { categories: respLabels },
    yaxis: { min: 0, labels: { formatter: (val) => String(Math.round(val)) } },
    colors: ['rgba(34,197,94,0.8)', 'rgba(239,68,68,0.8)'],
    legend: { position: 'top' },
    dataLabels: { enabled: false },
    tooltip: { shared: true, intersect: false },
  };

  // Age distribution histogram
  const ageBinLabels = AGE_BINS.map((bin, idx) =>
    idx === AGE_BINS.length - 1 ? `${bin}+` : `${bin}–${AGE_BINS[idx + 1]}`,
  );
  const ageCounts = new Array(AGE_BINS.length).fill(0);
  tickets.forEach((tkt) => {
    let idx = AGE_BINS.length - 1;
    for (let pos = AGE_BINS.length - 1; pos >= 0; pos--) {
      if (tkt.ageD >= AGE_BINS[pos]) {
        idx = pos;
        break;
      }
    }
    ageCounts[idx]++;
  });

  const ageDistOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: { bar: { borderRadius: 2, columnWidth: '70%' } },
    xaxis: { categories: ageBinLabels },
    yaxis: { min: 0, labels: { formatter: (val) => String(Math.round(val)) } },
    colors: ['rgba(18,32,72,0.78)'],
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card
        title={
          <span>
            SLA by priority{' '}
            {subTitle('resolution SLA = age vs. priority threshold')}
          </span>
        }
        size="small"
      >
        <Table
          dataSource={slaRows}
          columns={slaColumns}
          rowKey="pri"
          size="small"
          pagination={false}
        />
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card
          title={
            <span>
              Response SLA {subTitle('first-touch time vs. priority threshold')}
            </span>
          }
          size="small"
        >
          <ReactApexChart
            type="bar"
            height={260}
            series={[
              { name: 'Response met', data: respMet },
              { name: 'Response missed', data: respMiss },
            ]}
            options={respOptions}
          />
        </Card>

        <Card
          title={<span>Open age distribution {subTitle('days open')}</span>}
          size="small"
        >
          <ReactApexChart
            type="bar"
            height={260}
            series={[{ name: 'Tickets', data: ageCounts }]}
            options={ageDistOptions}
          />
        </Card>
      </div>
    </div>
  );
};

export default SlaTab;
